/**
 * Real ViewBindings — translate MNBP/1 domain methods onto live MIMap /
 * MISceneView instances (spec §4). Used by the embed page; headless
 * conformance hosts plug in MemoryViewBinding instead.
 */
import { MIMap } from '../core/MIMap';
import { MISceneView } from '../core/MISceneView';
import { Layer, VectorLayer, TileLayer, VectorTileLayer, TerrainLayer } from '../layers/Layer';
import { Tileset3DLayer } from '../layers/Layer3D';
import { GeoJsonSource, OsmSource, XYZSource, WMSSource, MVTSource, DEMSource } from '../sources/Source';
import { Style, Fill, Stroke, CircleStyle, TextStyle } from '../style/Style';
import { DrawInteraction } from '../interactions/Interaction';
import {
  BridgeMethodError, ERR_METHOD_NOT_FOUND, ERR_INVALID_PARAMS, ERR_VIEW_KIND, ERR_UNSUPPORTED, ERR_ENGINE,
} from './protocol';
import type { ViewBinding } from './BridgeHost';

// ─── Style translation ───────────────────────────────────────────────────

export interface StyleSpec {
  circle?: { color?: string; radius?: number; strokeColor?: string; strokeWidth?: number };
  line?: { color?: string; width?: number; dash?: number[] };
  fill?: { color?: string; opacity?: number; outlineColor?: string };
  text?: { field?: string; color?: string; size?: number; haloColor?: string; haloWidth?: number };
}

export function styleFromSpec(spec?: StyleSpec): Style {
  const s = spec ?? {};
  const opts: Record<string, any> = {};
  if (s.circle) {
    opts.image = new CircleStyle({
      radius: s.circle.radius ?? 6,
      fill: new Fill({ color: s.circle.color ?? '#e11d48' }),
      stroke: new Stroke({ color: s.circle.strokeColor ?? '#ffffff', width: s.circle.strokeWidth ?? 1.5 }),
    });
  }
  if (s.line) {
    opts.stroke = new Stroke({
      color: s.line.color ?? '#2563eb',
      width: s.line.width ?? 2,
      ...(s.line.dash ? { lineDash: s.line.dash } : {}),
    });
  }
  if (s.fill) {
    opts.fill = new Fill({ color: s.fill.color ?? 'rgba(37,99,235,0.35)' });
    if (s.fill.outlineColor && !opts.stroke) {
      opts.stroke = new Stroke({ color: s.fill.outlineColor, width: 1.5 });
    }
  }
  if (s.text?.field) {
    try {
      opts.text = new TextStyle({
        text: `{${s.text.field}}`,
        ...(s.text.color ? { fill: new Fill({ color: s.text.color }) } : {}),
        ...(s.text.size ? { font: `${s.text.size}px sans-serif` } : {}),
      } as any);
    } catch { /* text styling is best-effort in v1 */ }
  }
  if (!opts.image && !opts.stroke && !opts.fill) {
    // Sensible defaults for untyped data.
    opts.image = new CircleStyle({ radius: 6, fill: new Fill({ color: '#e11d48' }) });
    opts.stroke = new Stroke({ color: '#2563eb', width: 2 });
    opts.fill = new Fill({ color: 'rgba(37,99,235,0.25)' });
  }
  return new Style(opts);
}

// ─── GeoJSON helpers (no external deps) ──────────────────────────────────

function walkCoords(node: any, fn: (c: [number, number]) => void): void {
  if (!node) return;
  if (Array.isArray(node) && typeof node[0] === 'number') { fn(node as [number, number]); return; }
  if (Array.isArray(node)) { node.forEach((n) => walkCoords(n, fn)); return; }
  if (node.type === 'FeatureCollection') { (node.features ?? []).forEach((f: any) => walkCoords(f, fn)); return; }
  if (node.type === 'Feature') { walkCoords(node.geometry, fn); return; }
  if (node.coordinates) walkCoords(node.coordinates, fn);
  if (node.geometries) node.geometries.forEach((g: any) => walkCoords(g, fn));
}

export function geojsonBBox(gj: any): [number, number, number, number] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, seen = false;
  walkCoords(gj, ([x, y]) => {
    seen = true;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  });
  return seen ? [minX, minY, maxX, maxY] : null;
}

function toPlain(value: any): any {
  try { return JSON.parse(JSON.stringify(value ?? null)); } catch { return null; }
}

function featureArrayToFC(features: any[]): any {
  return { type: 'FeatureCollection', features: toPlain(features) ?? [] };
}

async function blobToDataUrl(blob: Blob, mime: string): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i++) {
    bin += String.fromCharCode(buf[i]);
  }
  return `data:${mime};base64,${btoa(bin)}`;
}

function req<T>(v: T | undefined | null, name: string): T {
  if (v === undefined || v === null) throw new BridgeMethodError(ERR_INVALID_PARAMS, `missing param '${name}'`);
  return v;
}

const DEFAULT_MARKER_COLOR = '#e11d48';
const MARKER_HIT_RADIUS_PX = 12;

interface MarkerRecord { id: string; lngLat: [number, number]; color: string; feature: any }
interface MarkerBucket { source: GeoJsonSource; layer: VectorLayer; ids: Set<string> }

// ─── 2D binding ──────────────────────────────────────────────────────────

export class MIMapBinding implements ViewBinding {
  readonly kind = '2d' as const;

  private layers = new Map<string, { layer: Layer; source?: any; type: string }>();
  private markers = new Map<string, MarkerRecord>();
  private markerBuckets = new Map<string, MarkerBucket>();
  private markerSeq = 0;

  private draw: { interaction: DrawInteraction | null; features: any[] } = { interaction: null, features: [] };
  private drawnSource: GeoJsonSource | null = null;
  private drawEmits = new Map<string, (data: unknown) => void>();
  private markerClickEmit: ((data: unknown) => void) | null = null;
  private markerClickKey: any = null;

  constructor(public map: MIMap) {}

  async call(method: string, p: Record<string, any>): Promise<unknown> {
    switch (method) {
      // ── camera ──
      case 'map.setCenter': this.map.setCenter(req(p.center, 'center'), !!p.animate); return {};
      case 'map.getCenter': return { center: this.map.getCenter() };
      case 'map.setZoom': this.map.setZoom(req(p.zoom, 'zoom'), !!p.animate); return {};
      case 'map.getZoom': return { zoom: this.map.getZoom() };
      case 'map.setRotation': this.map.setRotation(req(p.rotation, 'rotation'), !!p.animate); return {};
      case 'map.getRotation': return { rotation: this.map.getRotation() };
      case 'map.setPitch': this.map.setPitch(req(p.pitch, 'pitch')); return {};
      case 'map.getPitch': return { pitch: this.map.getPitch() };
      case 'map.getCamera': return { ...this.cameraState() };
      case 'map.flyTo':
        this.map.flyTo({ center: p.center, zoom: p.zoom, bearing: p.bearing, pitch: p.pitch, duration: p.duration });
        return {};
      case 'map.easeTo':
        this.map.easeTo({ center: p.center, zoom: p.zoom, bearing: p.bearing, pitch: p.pitch, duration: p.duration });
        return {};
      case 'map.jumpTo':
        this.map.jumpTo({ center: p.center, zoom: p.zoom, bearing: p.bearing, pitch: p.pitch });
        return {};
      case 'map.panBy': this.map.panBy(req(p.offset, 'offset'), { duration: p.duration }); return {};
      case 'map.fitExtent': {
        const e = req<number[]>(p.extent, 'extent');
        if (!Array.isArray(e) || e.length !== 4) throw new BridgeMethodError(ERR_INVALID_PARAMS, 'extent must be [minX,minY,maxX,maxY]');
        this.map.fitExtent({ minX: e[0], minY: e[1], maxX: e[2], maxY: e[3] },
          { padding: p.padding, duration: p.duration, maxZoom: p.maxZoom });
        return {};
      }
      case 'map.getExtent': {
        const e = this.map.getExtent();
        return { extent: [e.minX, e.minY, e.maxX, e.maxY] };
      }
      case 'map.stop': (this.map as any).stop?.(); return {};
      case 'map.setStyle': this.map.setStyle(req(p.style, 'style')); return {};
      case 'map.setProjection': (this.map as any).setProjection?.(req(p.projection, 'projection')); return {};
      case 'map.updateSize': this.map.updateSize(); return {};
      case 'map.showGISToolBar':
        this.map.showGISToolBar(p.options ?? { offset: p.offset, draggable: p.draggable });
        return {};
      case 'map.hideGISToolBar':
        this.map.hideGISToolBar();
        return {};

      // ── querying ──
      case 'map.queryRenderedFeatures':
        return { features: featureArrayToFC(this.map.queryRenderedFeatures(p.point, { layers: p.layers })) };
      case 'map.hitTest': {
        const [x, y] = req<[number, number]>(p.point, 'point');
        const t = typeof p.tolerance === 'number' ? p.tolerance : 5;
        const feats = this.map.queryRenderedFeatures(
          [[x - t, y - t], [x + t, y + t]] as any, { layers: p.layers });
        return { features: featureArrayToFC(feats) };
      }
      case 'map.toMapCoordinate': return { coord: this.map.toMapCoordinate(req(p.pixel, 'pixel')) };
      case 'map.toPixelCoordinate': return { pixel: this.map.toPixelCoordinate(req(p.coord, 'coord')) };

      // ── screenshot ──
      case 'bridge.screenshot': {
        const format = p.format === 'jpeg' ? 'jpeg' : 'png';
        const blob = await this.map.exportImage({ format, quality: p.quality });
        return { dataUrl: await blobToDataUrl(blob, format === 'jpeg' ? 'image/jpeg' : 'image/png') };
      }

      default:
        if (method.startsWith('layers.')) return this.callLayers(method, p);
        if (method.startsWith('markers.')) return this.callMarkers(method, p);
        if (method.startsWith('draw.')) return this.callDraw(method, p);
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  private cameraState() {
    return {
      center: this.map.getCenter(), zoom: this.map.getZoom(),
      rotation: this.map.getRotation(), pitch: this.map.getPitch(),
    };
  }

  // ── layers ──
  private callLayers(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'layers.addGeoJson': {
        const id = String(req(p.id, 'id'));
        if (this.layers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `layer '${id}' already exists`);
        const data = req(p.data, 'data');
        const source = new GeoJsonSource({ data });
        const layer = new VectorLayer({ id, source, style: styleFromSpec(p.style) } as any);
        this.map.addLayer(layer);
        this.layers.set(id, { layer, source, type: 'geojson' });
        if (p.fit) {
          const bb = geojsonBBox(data);
          if (bb) this.map.fitExtent({ minX: bb[0], minY: bb[1], maxX: bb[2], maxY: bb[3] }, { padding: 40 });
        }
        return { id };
      }
      case 'layers.addTile': {
        const id = String(req(p.id, 'id'));
        if (this.layers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `layer '${id}' already exists`);
        const kind = String(req(p.kind, 'kind'));
        let layer: Layer;
        if (kind === 'osm') {
          layer = new TileLayer({ id, source: new OsmSource() } as any);
        } else if (kind === 'xyz') {
          layer = new TileLayer({ id, source: new XYZSource({ url: req(p.url, 'url'), tileSize: p.tileSize }) } as any);
        } else if (kind === 'wms') {
          layer = new TileLayer({ id, source: new WMSSource({ url: req(p.url, 'url'), layers: p.layers ?? [] }) } as any);
        } else if (kind === 'mvt') {
          layer = new VectorTileLayer({
            id, source: new MVTSource({ url: req(p.url, 'url') }),
            ...(p.sourceLayer ? { sourceLayer: p.sourceLayer } : {}),
            style: styleFromSpec(p.style),
          } as any);
        } else if (kind === 'dem') {
          layer = new TerrainLayer({ id, source: new DEMSource({ url: req(p.url, 'url') }) as any, shading: 'hillshade' } as any);
        } else {
          throw new BridgeMethodError(ERR_INVALID_PARAMS, `unknown tile kind '${kind}'`);
        }
        this.map.addLayer(layer);
        if (typeof p.opacity === 'number') (layer as any).setOpacity?.(p.opacity);
        this.layers.set(id, { layer, type: kind });
        return { id };
      }
      case 'layers.addTileset3D':
        throw new BridgeMethodError(ERR_VIEW_KIND, 'layers.addTileset3D requires a 3d view');
      case 'layers.setData': {
        const entry = this.requireLayer(p);
        if (!entry.source?.setData) throw new BridgeMethodError(ERR_UNSUPPORTED, 'layer source has no setData');
        entry.source.setData(req(p.data, 'data'));
        return {};
      }
      case 'layers.setVisible': this.requireLayer(p).layer.setVisible(!!p.visible); return {};
      case 'layers.setOpacity': this.requireLayer(p).layer.setOpacity(Number(req(p.opacity, 'opacity'))); return {};
      case 'layers.remove': {
        const entry = this.requireLayer(p);
        this.map.removeLayer(entry.layer);
        this.layers.delete(String(p.id));
        return {};
      }
      case 'layers.clear': {
        this.layers.forEach((entry) => { try { this.map.removeLayer(entry.layer); } catch { /* detached */ } });
        this.layers.clear();
        return {};
      }
      case 'layers.list':
        return {
          layers: Array.from(this.layers.entries()).map(([id, e]) => ({
            id, type: e.type,
            visible: (e.layer as any).visible ?? true,
            opacity: (e.layer as any).opacity ?? 1,
          })),
        };
      default:
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  private requireLayer(p: Record<string, any>) {
    const id = String(req(p.id, 'id'));
    const entry = this.layers.get(id);
    if (!entry) throw new BridgeMethodError(ERR_ENGINE, `unknown layer '${id}'`);
    return entry;
  }

  // ── markers ──
  private callMarkers(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'markers.add': {
        const id = p.id ? String(p.id) : `m${++this.markerSeq}`;
        if (this.markers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `marker '${id}' already exists`);
        const lngLat = req<[number, number]>(p.lngLat, 'lngLat');
        const color = typeof p.color === 'string' ? p.color : DEFAULT_MARKER_COLOR;
        const feature = {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: lngLat },
          properties: { __migisMarkerId: id, label: p.label ?? null, popupHtml: p.popupHtml ?? null, ...(p.properties ?? {}) },
        };
        this.markers.set(id, { id, lngLat, color, feature });
        this.addToBucket(id, color);
        return { id };
      }
      case 'markers.update': {
        const id = String(req(p.id, 'id'));
        const rec = this.markers.get(id);
        if (!rec) throw new BridgeMethodError(ERR_ENGINE, `unknown marker '${id}'`);
        if (p.lngLat) { rec.lngLat = p.lngLat; rec.feature.geometry.coordinates = p.lngLat; }
        if (p.label !== undefined) rec.feature.properties.label = p.label;
        if (p.popupHtml !== undefined) rec.feature.properties.popupHtml = p.popupHtml;
        if (p.properties) Object.assign(rec.feature.properties, p.properties);
        if (typeof p.color === 'string' && p.color !== rec.color) {
          this.removeFromBucket(id, rec.color);
          rec.color = p.color;
          this.addToBucket(id, p.color);
        } else {
          this.refreshBucket(rec.color);
        }
        return {};
      }
      case 'markers.remove': {
        const id = String(req(p.id, 'id'));
        const rec = this.markers.get(id);
        if (!rec) throw new BridgeMethodError(ERR_ENGINE, `unknown marker '${id}'`);
        this.removeFromBucket(id, rec.color);
        this.markers.delete(id);
        return {};
      }
      case 'markers.clear': {
        this.markerBuckets.forEach((bucket) => {
          try { this.map.removeLayer(bucket.layer); } catch { /* detached */ }
        });
        this.markerBuckets.clear();
        this.markers.clear();
        return {};
      }
      default:
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  private bucketKey(color: string): string { return color.toLowerCase(); }

  private addToBucket(id: string, color: string): void {
    const key = this.bucketKey(color);
    let bucket = this.markerBuckets.get(key);
    if (!bucket) {
      const source = new GeoJsonSource({ data: { type: 'FeatureCollection', features: [] } as any });
      const layer = new VectorLayer({
        id: `__migis_markers_${this.markerBuckets.size}_${key.replace(/[^a-z0-9]/g, '')}`,
        source,
        style: styleFromSpec({ circle: { color, radius: 8, strokeColor: '#ffffff', strokeWidth: 2 } }),
      } as any);
      this.map.addLayer(layer);
      bucket = { source, layer, ids: new Set() };
      this.markerBuckets.set(key, bucket);
    }
    bucket.ids.add(id);
    this.refreshBucket(color);
  }

  private removeFromBucket(id: string, color: string): void {
    const key = this.bucketKey(color);
    const bucket = this.markerBuckets.get(key);
    if (!bucket) return;
    bucket.ids.delete(id);
    this.refreshBucket(color);
  }

  private refreshBucket(color: string): void {
    const bucket = this.markerBuckets.get(this.bucketKey(color));
    if (!bucket) return;
    const features = Array.from(bucket.ids).map((id) => this.markers.get(id)?.feature).filter(Boolean);
    bucket.source.setData({ type: 'FeatureCollection', features } as any);
  }

  // ── draw ──
  private emitDraw(type: string, data: unknown): void {
    this.drawEmits.get(type)?.(data);
  }

  private ensureDrawnLayer(): GeoJsonSource {
    if (!this.drawnSource) {
      this.drawnSource = new GeoJsonSource({ data: { type: 'FeatureCollection', features: [] } as any });
      const layer = new VectorLayer({ id: '__migis_drawn__', source: this.drawnSource, style: styleFromSpec() } as any);
      this.map.addLayer(layer);
    }
    return this.drawnSource;
  }

  private callDraw(method: string, p: Record<string, any>): unknown {
    switch (method) {
      case 'draw.start': {
        const type = String(req(p.type, 'type'));
        if (!['point', 'line', 'polygon'].includes(type)) {
          throw new BridgeMethodError(ERR_INVALID_PARAMS, `draw type must be point|line|polygon`);
        }
        this.teardownDraw();
        const interaction = new DrawInteraction({ type: type as any, maxPoints: p.maxPoints, minPoints: p.minPoints });
        this.map.addInteraction(interaction);
        interaction.on('drawpoint', (e: any) => this.emitDraw('draw.point', { coordinate: e.coordinate ?? null, points: e.points ?? [] }));
        interaction.on('drawend', (e: any) => {
          const feature = toPlain(e.feature);
          if (feature) {
            this.draw.features.push(feature);
            this.ensureDrawnLayer().setData({ type: 'FeatureCollection', features: this.draw.features } as any);
          }
          this.emitDraw('draw.create', { feature });
        });
        interaction.on('drawabort', () => this.emitDraw('draw.abort', {}));
        this.draw.interaction = interaction;
        return {};
      }
      case 'draw.stop': this.teardownDraw(); return {};
      case 'draw.finish': {
        const feature = this.draw.interaction ? toPlain(this.draw.interaction.finishDrawing()) : null;
        return { feature };
      }
      case 'draw.abort': this.draw.interaction?.abortDrawing(); return {};
      case 'draw.removeLastPoint': this.draw.interaction?.removeLastPoint(); return {};
      case 'draw.getFeatures': return { features: { type: 'FeatureCollection', features: [...this.draw.features] } };
      case 'draw.clear': {
        this.draw.features = [];
        this.drawnSource?.setData({ type: 'FeatureCollection', features: [] } as any);
        return {};
      }
      default:
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  private teardownDraw(): void {
    if (this.draw.interaction) {
      try { this.draw.interaction.abortDrawing(); } catch { /* not active */ }
      try { this.map.removeInteraction(this.draw.interaction as any); } catch { try { this.draw.interaction.destroy(); } catch { /* gone */ } }
      this.draw.interaction = null;
    }
  }

  // ── events ──
  subscribe(type: string, emit: (data: unknown) => void, opts?: { throttleMs?: number }): (() => void) | null {
    const pointerData = (e: any) => ({ lngLat: e.coordinate ?? null, point: e.pixel ?? null });

    switch (type) {
      case 'click': case 'dblclick': case 'contextmenu': {
        const handler = (e: any) => emit(pointerData(e));
        this.map.on(type, handler);
        return () => this.map.off(type, handler);
      }
      case 'pointermove': {
        const throttle = Math.max(16, opts?.throttleMs ?? 100);
        let last = 0;
        const handler = (e: any) => {
          const now = Date.now();
          if (now - last >= throttle) { last = now; emit(pointerData(e)); }
        };
        this.map.on('pointermove', handler);
        return () => this.map.off('pointermove', handler);
      }
      case 'movestart': {
        const handler = () => emit({});
        this.map.on('movestart', handler);
        return () => this.map.off('movestart', handler);
      }
      case 'moveend': {
        const handler = () => emit({ camera: this.cameraState() });
        this.map.on('moveend', handler);
        return () => this.map.off('moveend', handler);
      }
      case 'zoomchange': {
        const handler = () => emit({ zoom: this.map.getZoom() });
        this.map.on('zoomchange', handler);
        return () => this.map.off('zoomchange', handler);
      }
      case 'rotationchange': {
        const handler = () => emit({ rotation: this.map.getRotation() });
        this.map.on('rotationchange', handler);
        return () => this.map.off('rotationchange', handler);
      }
      case 'load': {
        this.map.whenReady(() => emit({}));
        return () => { /* one-shot */ };
      }
      case 'error': {
        const handler = (e: any) => emit({ message: e?.message ?? e?.error?.message ?? 'error' });
        this.map.on('error', handler);
        return () => this.map.off('error', handler);
      }
      case 'draw.point': case 'draw.create': case 'draw.abort': {
        this.drawEmits.set(type, emit);
        return () => this.drawEmits.delete(type);
      }
      case 'marker.click': {
        this.markerClickEmit = emit;
        if (!this.markerClickKey) {
          this.markerClickKey = this.map.on('click', (e: any) => this.hitTestMarkers(e));
        }
        return () => { this.markerClickEmit = null; };
      }
      default:
        return null;
    }
  }

  private hitTestMarkers(e: any): void {
    if (!this.markerClickEmit || !e?.pixel) return;
    const [px, py] = e.pixel;
    let best: { id: string; lngLat: [number, number]; d: number } | null = null;
    for (const rec of Array.from(this.markers.values())) {
      try {
        const [mx, my] = this.map.toPixelCoordinate(rec.lngLat);
        const d = Math.hypot(mx - px, my - py);
        if (d <= MARKER_HIT_RADIUS_PX && (!best || d < best.d)) best = { id: rec.id, lngLat: rec.lngLat, d };
      } catch { /* projection unavailable mid-move */ }
    }
    if (best) this.markerClickEmit({ id: best.id, lngLat: best.lngLat });
  }

  destroy(): void {
    this.teardownDraw();
    try { this.map.destroy(); } catch { /* container already gone */ }
  }
}

// ─── 3D binding ──────────────────────────────────────────────────────────

export class MISceneViewBinding implements ViewBinding {
  readonly kind = '3d' as const;
  private layers = new Map<string, { layer: any; type: string }>();

  constructor(public scene: MISceneView) {}

  async call(method: string, p: Record<string, any>): Promise<unknown> {
    switch (method) {
      case 'scene.setCamera': this.scene.setCamera(req(p.camera, 'camera'), !!p.animate); return {};
      case 'scene.getCamera': return { camera: toPlain(this.scene.getCamera()) };
      case 'scene.flyTo': this.scene.setCamera(req(p.camera, 'camera'), true); return {};
      case 'scene.lookAt': this.scene.lookAt(req(p.target, 'target')); return {};
      case 'scene.setTerrain': this.scene.setTerrain(req(p.options, 'options')); return {};
      case 'scene.setSceneMode': this.scene.setSceneMode(req(p.mode, 'mode'), p.duration ?? 0); return {};
      case 'scene.setNavigationMode': this.scene.setNavigationMode(req(p.mode, 'mode')); return {};
      case 'scene.setLightingTime': this.scene.setLightingTime(new Date(String(req(p.time, 'time')))); return {};
      case 'scene.getElevation': {
        const elevation = await this.scene.getElevation(req(p.coord, 'coord'));
        return { elevation: Number.isFinite(elevation) ? elevation : null };
      }
      case 'scene.screenToGeo': return { coord: this.scene.screenToGeo(req(p.pixel, 'pixel')) };
      case 'scene.geoToScreen': return { pixel: this.scene.geoToScreen(req(p.coord, 'coord')) };
      case 'scene.setGlobeShow': this.scene.setGlobeShow(!!p.show); return {};
      case 'scene.showGISToolBar':
        this.scene.showGISToolBar(p.options ?? { offset: p.offset, draggable: p.draggable });
        return {};
      case 'scene.hideGISToolBar':
        this.scene.hideGISToolBar();
        return {};

      case 'layers.addTileset3D': {
        const id = String(req(p.id, 'id'));
        if (this.layers.has(id)) throw new BridgeMethodError(ERR_ENGINE, `layer '${id}' already exists`);
        const layer = new Tileset3DLayer({ id, url: String(req(p.url, 'url')), ...(p.options ?? {}) } as any);
        (this.scene as any).addLayer(layer);
        this.layers.set(id, { layer, type: 'tileset3d' });
        return { id };
      }
      case 'layers.setVisible': this.requireLayer(p).layer.setVisible?.(!!p.visible); return {};
      case 'layers.setOpacity': this.requireLayer(p).layer.setOpacity?.(Number(req(p.opacity, 'opacity'))); return {};
      case 'layers.remove': {
        const entry = this.requireLayer(p);
        (this.scene as any).removeLayer(entry.layer);
        this.layers.delete(String(p.id));
        return {};
      }
      case 'layers.clear': {
        this.layers.forEach((e) => { try { (this.scene as any).removeLayer(e.layer); } catch { /* detached */ } });
        this.layers.clear();
        return {};
      }
      case 'layers.list':
        return { layers: Array.from(this.layers.entries()).map(([id, e]) => ({ id, type: e.type, visible: e.layer.visible ?? true, opacity: e.layer.opacity ?? 1 })) };

      case 'bridge.screenshot':
        throw new BridgeMethodError(ERR_UNSUPPORTED, 'screenshot supports 2d views in v1');

      default:
        if (method.startsWith('markers.') || method.startsWith('draw.') || method.startsWith('layers.')) {
          throw new BridgeMethodError(ERR_UNSUPPORTED, `${method} is not supported on 3d views in v1`);
        }
        throw new BridgeMethodError(ERR_METHOD_NOT_FOUND, `unknown method '${method}'`);
    }
  }

  private requireLayer(p: Record<string, any>) {
    const id = String(req(p.id, 'id'));
    const entry = this.layers.get(id);
    if (!entry) throw new BridgeMethodError(ERR_ENGINE, `unknown layer '${id}'`);
    return entry;
  }

  subscribe(type: string, emit: (data: unknown) => void): (() => void) | null {
    switch (type) {
      case 'ready': {
        const handler = () => emit({});
        this.scene.on('ready', handler);
        return () => this.scene.off('ready', handler);
      }
      case 'click': {
        const handler = (e: any) => emit({
          lngLat: Array.isArray(e.coordinate) ? [e.coordinate[0], e.coordinate[1]] : null,
          position: e.coordinate ?? null,
          point: e.pixel ?? null,
        });
        this.scene.on('click', handler);
        return () => this.scene.off('click', handler);
      }
      case 'camera-change': {
        const handler = () => emit({ camera: toPlain(this.scene.getCamera()) });
        this.scene.on('camera-change', handler);
        return () => this.scene.off('camera-change', handler);
      }
      case 'error': {
        const handler = (e: any) => emit({ message: e?.message ?? 'error' });
        this.scene.on('error', handler);
        return () => this.scene.off('error', handler);
      }
      default:
        return null;
    }
  }

  destroy(): void {
    try { this.scene.destroy(); } catch { /* container already gone */ }
  }
}

// analysis.* handler lives in an engine-free module so Node hosts can use it.
export { createSpatialAnalysisEngine } from './analysisEngine';
